<!-- eslint-disable max-lines -->
<script setup lang="ts" generic="Row extends Record<string, unknown>">
/* eslint-disable max-lines */
import { computed, nextTick, onMounted, ref, shallowRef, useSlots, watch } from 'vue'
import type { CSSProperties } from 'vue'

import { columnCellStyle, declaredTableWidth, usesDeclaredLayout } from './columnStyle'
import { compareCells } from './compareCells'
import Td from './Td.vue'
import Th from './Th.vue'
import { resolveTableHeaderPlan } from './types'
import type { DataTableProps, SortState, TableColumn } from './types'
import { useResizeColumn } from './useResizeColumn'

import Skeleton from '@/shared/ui/skeleton/Skeleton.vue'
import { useSortable } from '@/shared/ui/sortable'
import Spinner from '@/shared/ui/spinner/Spinner.vue'
import { OverlayPortal } from '@/shared/ui/overlay'

const props = withDefaults(defineProps<DataTableProps<Row>>(), {
  state: 'ready',
  sort: null,
  draggable: false,
  expandable: false,
  onlyOnOverflow: false,
  skeletonRows: 5,
  initialPageSize: 50,
  pageSize: 25,
  loadMoreThreshold: 48,
  defaultColumnWidth: 200,
  defaultTextColumnWidth: 224,
  clientSort: true,
  density: 'default',
  interactiveRows: true,
  sortCycle: 'binary',
  initialSortDirection: 'asc',
  refreshing: false,
})

const emit = defineEmits<{
  'update:sort': [sort: SortState]
  reorder: [columns: TableColumn<Row>[]]
  resize: [columns: TableColumn<Row>[]]
  freeze: [column: TableColumn<Row>, frozen: boolean]
  'expand-column': [column: TableColumn<Row>, expanded: boolean]
  'row-click': [row: Row]
  'load-more': []
  retry: []
  expand: [row: Row, expanded: boolean]
}>()

const slots = useSlots()
const EXPAND_ICON = '+'
const COLLAPSE_ICON = '−'
const ROOT_CLASS =
  'ui-data-table w-full overflow-auto border border-border rounded-md bg-surface font-sans'
const TABLE_CLASS = 'min-w-full border-separate border-spacing-0'
const STATE_CLASS = 'ui-data-table__state p-lg text-muted text-center bg-surface'
const EXPANDED_CLASS =
  'ui-data-table__expanded p-lg text-muted bg-surface text-start border-b border-border'
const TFOOT_CLASS =
  'sticky z-20 bottom-0 [&_.ui-table-td]:font-medium [&_.ui-table-td]:bg-surface-sunken [&_.ui-table-td]:border-t [&_.ui-table-td]:border-t-[var(--ui-table-grid)]'
const ROW_CLASS = 'ui-data-table__row [&:hover_.ui-table-td]:bg-surface-sunken'
const CHILD_ROW_CLASS = `${ROW_CLASS} ui-data-table__row--child [&_.ui-table-td:first-child]:ps-xl`
const rowInteractiveClass = computed(() =>
  props.interactiveRows ? 'ui-data-table__row--interactive cursor-pointer' : '',
)
const activeSort = ref<SortState>(props.sort)
const localColumns = shallowRef<TableColumn<Row>[]>(props.columns.map((column) => ({ ...column })))
const expandedKeys = ref(new Set<string | number>(props.defaultExpandedKeys ?? []))
const visibleCount = ref(props.initialPageSize)
const loadRequested = ref(false)
const resize = useResizeColumn()
const headerPlan = computed(() => resolveTableHeaderPlan(localColumns.value, props.columnGroups))

const renderedColumns = computed(() => headerPlan.value.columns)
const sortable = useSortable(() => renderedColumns.value)

watch(
  () => props.sort,
  (sort) => (activeSort.value = sort),
)
watch(
  () => props.columns,
  (columns) => (localColumns.value = columns.map((column) => ({ ...column }))),
)
watch(
  () => props.rows.length,
  () => {
    // A grant following our own `load-more` reveals immediately (same scroll position, more
    // data); any other row-count change (filter, sort, fresh query) restarts at `initialPageSize`.
    visibleCount.value = loadRequested.value
      ? Math.max(visibleCount.value, props.rows.length)
      : props.initialPageSize
    loadRequested.value = false
    void fillViewportIfShort()
  },
)
watch(
  () => props.defaultExpandedKeys,
  (keys) => (expandedKeys.value = new Set(keys ?? [])),
  { deep: true },
)

const resolvedState = computed(() => {
  if (props.state !== 'ready') return props.state
  return props.rows.length === 0 && !props.footerRows?.length ? 'empty' : 'ready'
})
// Only meaningful once the previous response is actually on screen — a refetch that raced ahead
// of the first response still gets the skeleton, not a backdrop over nothing.
const showRefreshOverlay = computed(() => props.refreshing && resolvedState.value !== 'loading')

const sortedRows = computed(() => {
  const sort = activeSort.value
  if (!props.clientSort || !sort) return props.rows
  const direction = sort.dir === 'asc' ? 1 : -1
  return [...props.rows].sort(
    (left, right) => direction * compareCells(left[sort.field], right[sort.field]),
  )
})

const visibleRows = computed(() => sortedRows.value.slice(0, visibleCount.value))
const columnCount = computed(() => renderedColumns.value.length)

const keyFor = (row: Row, index: number): string | number => props.rowKey?.(row, index) ?? index

function isFrozen(column: TableColumn<Row>): boolean {
  return column.frozen === true || props.frozenColumns?.includes(column.field) === true
}

const columnExpandableSet = computed(() => new Set(props.columnExpandableFields ?? []))
const columnExpandedSet = computed(() => new Set(props.columnExpandedFields ?? []))
const isColumnExpandable = (column: TableColumn<Row>): boolean =>
  columnExpandableSet.value.has(column.field)
const isColumnExpanded = (column: TableColumn<Row>): boolean =>
  columnExpandedSet.value.has(column.field)

function tdCommon(column: TableColumn<Row>, rowIndex = 0) {
  return {
    align: column.align ?? (column.numeric ? 'end' : 'start'),
    numeric: column.numeric ?? false,
    frozen: isFrozen(column),
    cellStyle: cellStyle(column),
    density: props.density,
    striped: rowIndex % 2 === 0,
    supplementary: column.supplementary ?? false,
  }
}

const widthFor = (column: TableColumn<Row>): number =>
  column.width ?? (column.numeric ? props.defaultColumnWidth : props.defaultTextColumnWidth)
const widthDefaults = computed(() => ({
  metric: props.defaultColumnWidth,
  text: props.defaultTextColumnWidth,
}))
const declared = computed(() => usesDeclaredLayout(renderedColumns.value))
const tableClass = computed(() => [TABLE_CLASS, declared.value ? 'table-fixed' : 'w-max'])
const tableStyle = computed<CSSProperties | undefined>(() => {
  const width = declaredTableWidth(renderedColumns.value, widthDefaults.value)
  return width === undefined ? undefined : { width }
})

function cellStyle(column: TableColumn<Row>): CSSProperties {
  return columnCellStyle(column, renderedColumns.value, isFrozen, widthDefaults.value)
}

// `Th` is itself generic; Vue's template-generic inference doesn't link its `Row` to this
// component's, so `TableColumn<Row>` narrows against `Th`'s default `TableColumn<Record<string,
// unknown>>` prop type under `exactOptionalPropertyTypes`. Erase to the common shape at the edge.
function asThColumn(column: TableColumn<Row>): TableColumn<Record<string, unknown>> {
  return column as TableColumn<Record<string, unknown>>
}

function toggleSort(column: TableColumn<Row>): void {
  if (!column.sortable) return
  const current = activeSort.value
  let next: SortState
  if (current?.field !== column.field) {
    next = { field: column.field, dir: props.initialSortDirection }
  } else if (current.dir === props.initialSortDirection) {
    next = {
      field: column.field,
      dir: props.initialSortDirection === 'asc' ? 'desc' : 'asc',
    }
  } else if (props.sortCycle === 'triState') {
    next = null
  } else {
    next = { field: column.field, dir: props.initialSortDirection }
  }
  activeSort.value = next
  emit('update:sort', next)
}

function dropColumn(index: number): void {
  const move = sortable.drop(index)
  if (!move) return
  localColumns.value = move.items
  emit('reorder', move.items)
}

function startResize(event: PointerEvent, column: TableColumn<Row>): void {
  resize.start(event, {
    width: widthFor(column),
    ...(column.minWidth === undefined ? {} : { minWidth: column.minWidth }),
    ...(column.maxWidth === undefined ? {} : { maxWidth: column.maxWidth }),
    onResize: (width) => {
      localColumns.value = localColumns.value.map((item) =>
        item.field === column.field ? { ...item, width } : item,
      )
      emit('resize', localColumns.value)
    },
  })
}

function isExpanded(row: Row, index: number): boolean {
  return expandedKeys.value.has(keyFor(row, index))
}

function canExpand(row: Row): boolean {
  return props.expandable && ((props.getChildren?.(row).length ?? 0) > 0 || Boolean(slots.expanded))
}

function toggleExpanded(row: Row, index: number): void {
  const key = keyFor(row, index)
  const next = new Set(expandedKeys.value)
  const expanded = !next.has(key)
  if (expanded) next.add(key)
  else next.delete(key)
  expandedKeys.value = next
  emit('expand', row, expanded)
}

function tooltipFor(row: Row, column: TableColumn<Row>): string | undefined {
  if (slots[`cell-${column.field}`]) return undefined
  const value = row[column.field]
  return value == null ? undefined : String(value)
}

function requestLoadMoreIfExhausted(): void {
  if (visibleCount.value < sortedRows.value.length) return
  if (loadRequested.value || props.totalRows === undefined || props.totalRows <= props.rows.length)
    return
  loadRequested.value = true
  emit('load-more')
}

function onScroll(event: Event): void {
  const target = event.currentTarget as HTMLElement
  if (target.scrollHeight - target.scrollTop - target.clientHeight > props.loadMoreThreshold) return
  if (visibleCount.value < sortedRows.value.length) {
    visibleCount.value = Math.min(visibleCount.value + props.pageSize, sortedRows.value.length)
    return
  }
  requestLoadMoreIfExhausted()
}

const root = ref<HTMLElement | null>(null)

// A short result (e.g. a 10-row opening batch on a tall page) can leave nothing to overflow, so
// `onScroll` never fires and the user has no way to ask for more. Grant (then request) another
// batch, re-measuring after each, until it overflows, there's nothing left, or the cap is hit.
async function fillViewportIfShort(): Promise<void> {
  await nextTick()
  for (let guard = 0; guard < 100; guard++) {
    const el = root.value
    if (
      resolvedState.value !== 'ready' ||
      !el ||
      el.scrollHeight - el.clientHeight > props.loadMoreThreshold
    )
      return
    if (visibleCount.value >= sortedRows.value.length) return requestLoadMoreIfExhausted()
    visibleCount.value = Math.min(visibleCount.value + props.pageSize, sortedRows.value.length)
    await nextTick()
  }
}

onMounted(fillViewportIfShort)
</script>

<template>
  <div v-if="columnCount > 0" class="ui-data-table-wrapper relative">
    <div
      ref="root"
      :class="[ROOT_CLASS, `ui-data-table--${density}`]"
      :style="maxHeight ? { maxHeight } : undefined"
      @scroll.passive="onScroll"
    >
      <OverlayPortal>
        <div
          v-if="resize.resizing.value"
          class="fixed inset-0 z-toast cursor-ew-resize"
          aria-hidden="true"
        />
      </OverlayPortal>
      <table :class="tableClass" :style="tableStyle" :aria-label="ariaLabel">
        <thead class="sticky z-30 top-0">
          <tr v-for="(headerRow, headerRowIndex) in headerPlan.rows" :key="headerRowIndex">
            <template
              v-for="cell in headerRow"
              :key="cell.type === 'column' ? cell.column.field : cell.group.key"
            >
              <Th
                v-if="cell.type === 'column'"
                :column="asThColumn(cell.column)"
                :rowspan="cell.rowspan"
                :sort="activeSort"
                :cell-style="cellStyle(cell.column)"
                :draggable="draggable"
                :frozen="isFrozen(cell.column)"
                :freeze-enabled="freezeEnabled ?? false"
                :freeze-label="freezeLabel?.(cell.column, isFrozen(cell.column))"
                :resize-label="resizeLabel?.(cell.column)"
                :density="density"
                :column-expandable="isColumnExpandable(cell.column)"
                :column-expanded="isColumnExpanded(cell.column)"
                :column-expand-label="
                  columnExpandLabel?.(cell.column, isColumnExpanded(cell.column))
                "
                @sort="toggleSort(cell.column)"
                @dragstart="sortable.begin(cell.columnIndex)"
                @dragover="sortable.over(cell.columnIndex)"
                @drop="dropColumn(cell.columnIndex)"
                @dragend="sortable.cancel"
                @resize="startResize($event, cell.column)"
                @freeze="emit('freeze', cell.column, !isFrozen(cell.column))"
                @expand-column="emit('expand-column', cell.column, !isColumnExpanded(cell.column))"
              >
                <slot :name="`header-${cell.column.field}`" :column="cell.column">{{
                  cell.column.header
                }}</slot>
              </Th>
              <th
                v-else
                class="ui-data-table__column-group py-xs px-md text-[var(--ui-table-header-fg)] bg-[var(--ui-table-header-bg)] border-b border-border text-[length:var(--ui-table-header-size)] font-[var(--ui-table-header-weight)] text-center whitespace-nowrap"
                scope="colgroup"
                :colspan="cell.columns.length"
              >
                <slot
                  :name="`header-group-${cell.group.key}`"
                  :group="cell.group"
                  :columns="cell.columns"
                >
                  {{ cell.group.header }}
                </slot>
              </th>
            </template>
          </tr>
        </thead>
        <tbody v-if="resolvedState === 'loading'">
          <tr v-for="rowIndex in skeletonRows" :key="rowIndex">
            <Td
              v-for="column in renderedColumns"
              :key="column.field"
              v-bind="tdCommon(column)"
              :tooltip="undefined"
              :only-on-overflow="false"
            >
              <slot name="loading" :column="column" :row-index="rowIndex"><Skeleton /></slot>
            </Td>
          </tr>
        </tbody>
        <tbody v-else-if="resolvedState === 'empty' || resolvedState === 'error'">
          <tr>
            <td :class="STATE_CLASS" :colspan="columnCount">
              <slot v-if="resolvedState === 'empty'" name="empty" />
              <slot v-else name="error" :retry="() => emit('retry')" />
            </td>
          </tr>
        </tbody>
        <tbody v-else>
          <template v-for="(row, rowIndex) in visibleRows" :key="keyFor(row, rowIndex)">
            <tr :class="[ROW_CLASS, rowInteractiveClass]" @click="emit('row-click', row)">
              <Td
                v-for="(column, columnIndex) in renderedColumns"
                :key="column.field"
                v-bind="tdCommon(column, rowIndex)"
                :tooltip="tooltipFor(row, column)"
                :only-on-overflow="column.onlyOnOverflow ?? onlyOnOverflow"
                :wrap="column.wrap ?? false"
              >
                <button
                  v-if="columnIndex === 0 && canExpand(row)"
                  class="ui-data-table__expand inline-grid place-items-center w-6 h-6 me-xs text-info bg-info-soft border-0 rounded-sm cursor-pointer focus-visible:outline-[length:var(--ui-ring-width)] focus-visible:outline-solid focus-visible:outline-ring focus-visible:outline-offset-2"
                  type="button"
                  :aria-expanded="isExpanded(row, rowIndex)"
                  :aria-label="
                    (isExpanded(row, rowIndex) ? collapseLabel?.(row) : expandLabel?.(row)) ??
                    column.header
                  "
                  @click.stop="toggleExpanded(row, rowIndex)"
                >
                  <span aria-hidden="true">
                    {{ isExpanded(row, rowIndex) ? COLLAPSE_ICON : EXPAND_ICON }}
                  </span>
                </button>
                <slot
                  :name="`cell-${column.field}`"
                  :row="row"
                  :value="row[column.field]"
                  :column="column"
                  :row-index="rowIndex"
                >
                  {{ row[column.field] }}
                </slot>
              </Td>
            </tr>
            <tr
              v-for="(child, childIndex) in isExpanded(row, rowIndex)
                ? (getChildren?.(row) ?? [])
                : []"
              :key="`${String(keyFor(row, rowIndex))}-child-${String(keyFor(child, childIndex))}`"
              :class="[CHILD_ROW_CLASS, rowInteractiveClass]"
              @click="emit('row-click', child)"
            >
              <Td
                v-for="column in renderedColumns"
                :key="column.field"
                v-bind="tdCommon(column, rowIndex)"
                :tooltip="tooltipFor(child, column)"
                :only-on-overflow="column.onlyOnOverflow ?? onlyOnOverflow"
                :wrap="column.wrap ?? false"
              >
                <slot
                  :name="`cell-${column.field}`"
                  :row="child"
                  :value="child[column.field]"
                  :column="column"
                  :row-index="childIndex"
                >
                  {{ child[column.field] }}
                </slot>
              </Td>
            </tr>
            <tr v-if="isExpanded(row, rowIndex) && $slots.expanded">
              <td :class="EXPANDED_CLASS" :colspan="columnCount">
                <slot name="expanded" :row="row" />
              </td>
            </tr>
          </template>
        </tbody>
        <tfoot v-if="resolvedState === 'ready' && footerRows?.length" :class="TFOOT_CLASS">
          <tr v-for="(row, rowIndex) in footerRows" :key="`footer-${keyFor(row, rowIndex)}`">
            <Td
              v-for="column in renderedColumns"
              :key="column.field"
              v-bind="tdCommon(column)"
              :tooltip="undefined"
              :only-on-overflow="false"
            >
              <slot
                :name="`footer-${column.field}`"
                :row="row"
                :value="row[column.field]"
                :column="column"
                :row-index="rowIndex"
              >
                {{ row[column.field] }}
              </slot>
            </Td>
          </tr>
        </tfoot>
      </table>
    </div>
    <div
      v-if="showRefreshOverlay"
      class="ui-data-table__refresh-overlay absolute inset-0 z-40 flex items-center justify-center rounded-md bg-surface/60"
      role="status"
      aria-live="polite"
    >
      <Spinner size="md" />
    </div>
  </div>
</template>
